import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SalesforceStageMapping,
  salesforceStageMappingsQueryKeys,
} from '@/hooks/salesforce/types'
import { useHasSalesforceIntegration } from '@/hooks/salesforce/useSalesforceIntegration'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'

interface UseSalesforceStageMappingsArgs<T> {
  orgId: UUID | ''
  select?: (data: PaginatedResponse<SalesforceStageMapping>) => T
}

export const fetchSalesforceStageMappings = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<SalesforceStageMapping>> => {
  if (!orgId) throw new Error('No organization ID provided.')
  const url = `${API_URL}/api/v1/organizations/${orgId}/salesforce/opportunity-stage-mappings`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as PaginatedResponse<SalesforceStageMapping>
}

export const useSalesforceStageMappings = <T = SalesforceStageMapping[]>({
  orgId,
  select,
}: UseSalesforceStageMappingsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const { data: hasSalesforceIntegration } = useHasSalesforceIntegration({
    orgId,
  })

  const selectFn = useCallback(
    (data: PaginatedResponse<SalesforceStageMapping>) => {
      if (select) {
        return select(data) as unknown as T
      }
      return data.data as unknown as T
    },
    [select],
  )
  return useQuery({
    queryKey: salesforceStageMappingsQueryKeys.organization({ orgId }),
    queryFn: () => fetchSalesforceStageMappings(orgId, accessToken),
    enabled: !!orgId && Boolean(hasSalesforceIntegration),
    staleTime: Infinity,
    select: selectFn,
  })
}
